import "./tease-carousel.scss";

import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { PaginationData, type Splide as SplideType } from "@splidejs/splide";
import { Splide, SplideTrack } from "@splidejs/react-splide";
import "@splidejs/react-splide/css/core";
import {
  ReactNode,
  Children,
  useRef,
  HTMLAttributes,
  createContext,
  MutableRefObject,
} from "react";
import cn from "clsx";
import { useResizeObserver } from "design-system/hooks/use-resize-observer";

import { ArrowButton } from "design-system/components/primitives/arrow-button/arrow-button";
import { Alignment } from "design-system/types/types";

export interface IndicatorLogoItem {
  logo: ReactNode;
  company: string;
}

interface CarouselContextProps {
  itemsRef?: MutableRefObject<Array<HTMLDivElement>>;
  numItems?: number;
}

const gap = 16;

export const CarouselContext = createContext<CarouselContextProps>({});

export interface CarouselProps extends HTMLAttributes<HTMLDivElement> {
  description?: ReactNode;
  /** One or more `Carousel.Item`s */
  children: ReactNode;
  align?: Alignment;
  itemsPerPage?: number;
  customIndicatorLogos?: Array<IndicatorLogoItem>;
  showIndicatorLogos?: boolean;
  className?: string;
}

/**
 * ## See it in use on...
 * - A [landing page](/story/example-pages-landing-pages-mba-entrepreneurship--story)
 *
 * - **`id: CB-018-000-00`**
 * - **`data-region: cb__tease-carousel`**
 */
export function TeaseCarousel({
  description,
  children,
  align = "full",
  itemsPerPage,
  customIndicatorLogos,
  showIndicatorLogos,
  className,
}: CarouselProps) {
  const ref = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<Array<HTMLDivElement>>([]);

  const item = itemsRef.current.length ? itemsRef.current[0] : null;

  const itemWidth = item ? item.clientWidth + gap : 0;

  // Use carousel width to determine the appropriate number of items to display per page
  const { width } = useResizeObserver(ref);

  const isInitializing = itemWidth === 0 || width === 0;
  const perPage =
    itemsPerPage ||
    Math.min(
      itemWidth ? Math.floor(width / itemWidth) : 0,
      itemsRef.current.length ?? 1,
    );
  const numItems = Children.count(children);
  const pageCount = perPage ? Math.ceil(numItems / perPage) : 0;

  const wrapperClass = cn(
    `hbs-global-align-${align}`,
    "hbs-component--carousel",
    className && className,
  );

  const hasMultiplePages = pageCount > 1 && !isInitializing;

  return (
    <div className={wrapperClass} data-region="cb__tease-carousel">
      <div className="hbs-tease-carousel" ref={ref}>
        {description}
        <div
          className={cn(
            "hbs-tease-carousel__container",
            isInitializing && "hbs-tease-carousel__container--initiating",
          )}
        >
          <CarouselContext.Provider value={{ itemsRef, numItems }}>
            <Splide
              options={{
                type: isInitializing || pageCount > 1 ? "loop" : undefined,
                gap: `${gap}px`,
                drag: isInitializing || pageCount > 1 ? "free" : undefined,
                snap: true,
                perPage: perPage || 1,
                autoWidth: itemsPerPage ? false : true,
                padding: itemsPerPage ? undefined : { right: "20%" },
                arrows: hasMultiplePages,
                classes: {
                  page: "splide__pagination__page hbs-tease-carousel__indicator",
                },
                speed: 600,
              }}
              hasTrack={false}
              onPaginationMounted={(
                splide: SplideType,
                data: PaginationData,
              ) => {
                data.items.forEach(function (item) {
                  const customIndicator = customIndicatorLogos?.[item.page];

                  if (customIndicator) {
                    // Splide doesn't provide a way to set the content of
                    // pagination using react, so we need to use plan JS APIs
                    // https://react.dev/reference/react-dom/server/renderToString#removing-rendertostring-from-the-client-code
                    const root = createRoot(item.button);
                    flushSync(() => {
                      root.render(customIndicator.logo);
                    });
                  } else {
                    item.button.innerHTML = "—";
                  }
                });
              }}
            >
              <SplideTrack>{children}</SplideTrack>

              <div
                className="hbs-tease-carousel__controls"
                aria-hidden={isInitializing}
              >
                <ul
                  className={cn(
                    "splide__pagination hbs-tease-carousel__indicators",
                    customIndicatorLogos &&
                      showIndicatorLogos &&
                      "hbs-tease-carousel__indicators--with-logos",
                  )}
                  data-testid="carousel-indicators"
                ></ul>

                <div className="hbs-tease-carousel__pager splide__arrows">
                  <ArrowButton
                    variant="prev"
                    className="hbs-tease-carousel__button--prev splide__arrow splide__arrow--prev"
                    data-testid="carousel-button-left"
                  />
                  <ArrowButton
                    variant="next"
                    className="hbs-tease-carousel__button--next splide__arrow splide__arrow--next"
                    data-testid="carousel-button-right"
                  />
                </div>
              </div>
            </Splide>
          </CarouselContext.Provider>
        </div>
      </div>
    </div>
  );
}
